/**
 * TransactionsPane.tsx — per-creator transactions ("Notifications") feed (Surface B).
 *
 * Relocated from the chatter app into @crm/chat (2026-07-20) so BOTH the chatter surface and
 * the admin full-parity chat surface use ONE implementation. Wired into ChatWorkspace as an
 * optional injected slot. Navigation is parameterized by `basePath` (defaults to '/inbox' for
 * the chatter surface; the admin surface passes '/chats') so tap-to-thread lands on the host's
 * own router prefix instead of a hardcoded '/inbox'.
 *
 * Shows a per-transaction list for the active creator: avatar · full fan name · kind badge ·
 * net (payout) amount · time · status (recent-first). Owner 2026-07-04: the row shows ONE
 * money figure — Maloum's net = creator payout (amount_payout_minor). The VAT+fee-inclusive
 * gross (amount_gross_minor) is never displayed, matching the admin Revenue page. Tapping a row
 * opens that fan's conversation:
 *   - rows with a fanConversationId navigate straight to the thread (D-07-16);
 *   - "no thread" rows (fan paid but the chat was never ingested) call POST
 *     /conversations/resolve to resolve/open the chat (owner 2026-07-03). When live sync is
 *     disabled/blocked the row shows a small inline notice instead of a dead tap.
 *
 * INVARIANT (D-07-13): NO period totals, NO breakdown chart, NO top-spenders/whale/ARPU
 * anywhere in this component. Per-transaction list ONLY.
 *
 * Analog: ConversationListPane.tsx (avatar row pattern, badge styling, navigate on click).
 */

import * as React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { conversationsApi, transactionsApi } from '../lib/api-client.js';
import type { ChatterTransactionDto } from '../lib/api-client.js';
import { formatEurMinor, fanDisplayName } from '../lib/format.js';
import { formatBerlin } from '../lib/time.js';
import { FanAvatar } from './FanAvatar.js';
import { Button } from '@crm/ui';

// ---------------------------------------------------------------------------
// Kind badge color classes (mirror ConversationListPane REPLY-badge classes)
// ---------------------------------------------------------------------------

const KIND_CLASSES: Record<string, string> = {
  subscription: 'bg-accent/15 text-accent',
  ppv_unlock: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  tip: 'bg-green-500/15 text-green-600 dark:text-green-400',
  other: 'bg-text-muted/10 text-text-muted',
};

const KIND_LABELS: Record<string, string> = {
  subscription: 'Sub',
  ppv_unlock: 'PPV',
  tip: 'Tip',
  other: 'Other',
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface TransactionsPaneProps {
  creatorId: string;
  creatorName: string;
  /**
   * Route prefix this instance is mounted under (mirrors ChatWorkspace.basePath). Tap-to-thread
   * builds `${basePath}/:creatorId/c/:conversationId`. Defaults to '/inbox' (chatter surface).
   */
  basePath?: string;
  /** ISO string of when this creator's transactions were last viewed (for new-row marking). */
  lastViewedAt?: string | null;
  /**
   * All-creators mode (D-13-03/06): the feed queries transactionsApi.allChatterFeed() (the
   * assignment-scoped merged feed across every assigned creator) instead of the single-
   * creator chatterFeed(creatorId). Each row shows its OWN creator label (D-13-08 token
   * treatment) and tap-to-thread navigates using the ROW's own creatorId, not the `creatorId`
   * prop (which the caller may pass as '' in this mode — Open Question 1). Defaults false —
   * the single-creator path is byte-identical when omitted.
   */
  allCreators?: boolean;
}

// ---------------------------------------------------------------------------
// TransactionsPane
// ---------------------------------------------------------------------------

export function TransactionsPane({
  creatorId,
  creatorName,
  basePath = '/inbox',
  lastViewedAt,
  allCreators = false,
}: TransactionsPaneProps) {
  const navigate = useNavigate();

  // WR-01: track accumulated pages + cursor for load-more pagination.
  const [pages, setPages] = React.useState<ChatterTransactionDto[][]>([]);
  const [nextCursor, setNextCursor] = React.useState<string | null>(null);
  const [loadingMore, setLoadingMore] = React.useState(false);

  // tap-to-thread resolve state (per row): which row is resolving + any inline notice.
  const [resolvingId, setResolvingId] = React.useState<string | null>(null);
  const [notices, setNotices] = React.useState<Record<string, string>>({});

  const txQuery = useQuery({
    queryKey: allCreators ? ['chatter-transactions', 'all'] : ['chatter-transactions', creatorId],
    queryFn: () => (allCreators ? transactionsApi.allChatterFeed() : transactionsApi.chatterFeed(creatorId)),
    enabled: allCreators ? true : !!creatorId,
    staleTime: 60_000,
  });

  // Reset pages whenever the primary query fires (creatorId change or stale refresh)
  React.useEffect(() => {
    if (txQuery.data) {
      setPages([txQuery.data.data]);
      setNextCursor(txQuery.data.next);
      setNotices({});
    }
  }, [txQuery.data]);

  const transactions = React.useMemo(() => pages.flat(), [pages]);

  const resolveMut = useMutation({
    mutationFn: (vars: { accountId: string; fanMemberId: string }) =>
      conversationsApi.resolve(vars.accountId, vars.fanMemberId),
  });

  async function handleLoadMore() {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const result = allCreators
        ? await transactionsApi.allChatterFeed(nextCursor, 20)
        : await transactionsApi.chatterFeed(
            creatorId, undefined, undefined, undefined, 20, nextCursor,
          );
      setPages((prev) => [...prev, result.data]);
      setNextCursor(result.next);
    } finally {
      setLoadingMore(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const isNew = (tx: ChatterTransactionDto): boolean => {
    if (!lastViewedAt) return true; // no marker → all are new
    return new Date(tx.occurredAt) > new Date(lastViewedAt);
  };

  const setNotice = (txId: string, message: string | null) => {
    setNotices((prev) => {
      const next = { ...prev };
      if (message == null) delete next[txId];
      else next[txId] = message;
      return next;
    });
  };

  const handleOpen = async (tx: ChatterTransactionDto) => {
    setNotice(tx.id, null);
    // All-creators mode (Open Question 1): navigate using the ROW's own creatorId so
    // tapping lands on that creator's own tab, not the `creatorId` prop (which the caller
    // passes as '' in this mode).
    const rowCreatorId = allCreators ? tx.creatorId : creatorId;

    // Already-ingested thread → open it directly (works today, no egress).
    if (tx.fanConversationId && rowCreatorId) {
      navigate(`${basePath}/${rowCreatorId}/c/${tx.fanConversationId}`);
      return;
    }
    // No Maloum member id → nothing to resolve against.
    if (!tx.fanMemberId) {
      setNotice(tx.id, 'This fan has no Maloum id to open a chat with.');
      return;
    }

    // "No thread" → resolve/open the fan's chat (live-gated server-side).
    setResolvingId(tx.id);
    try {
      const res = await resolveMut.mutateAsync({
        accountId: tx.accountId,
        fanMemberId: tx.fanMemberId,
      });
      if (res.status === 'resolved' && res.conversationId && rowCreatorId) {
        navigate(`${basePath}/${rowCreatorId}/c/${res.conversationId}`);
      } else {
        setNotice(tx.id, res.reason ?? 'Could not open this chat yet.');
      }
    } catch (err) {
      setNotice(tx.id, err instanceof Error ? err.message : 'Could not open this chat.');
    } finally {
      setResolvingId(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Loading / error states
  // ---------------------------------------------------------------------------

  return (
    <div className="flex flex-col h-full bg-bg-card">
      {/* Header */}
      <div className="px-3 py-2 flex items-center justify-between gap-2 shrink-0 border-b border-border">
        <span className="text-xs font-semibold uppercase tracking-wide text-text-subtle font-mono truncate">
          {creatorName} · Notifications
        </span>
        <div className="flex items-center gap-2 shrink-0">
          {/* Demo data badge (D-07-07). Cast keeps this shared package free of vite/client
              types while the consuming vite app still statically replaces import.meta.env. */}
          {(import.meta as unknown as { env?: { VITE_DEMO_DATA?: string } }).env?.VITE_DEMO_DATA === 'true' && (
            <span
              className="text-[10px] font-semibold px-2 py-0.5 rounded bg-warning/20 text-warning border border-warning/30"
              aria-label="Demo data — not live earnings"
            >
              Demo data — not live earnings
            </span>
          )}
          {/* WR-01: show "N+" when more pages exist to avoid implying a false total */}
          <span className="text-xs text-text-subtle">
            {nextCursor ? `${transactions.length}+` : `${transactions.length}`}
          </span>
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {txQuery.isLoading ? (
          <div className="flex items-center justify-center py-16">
            <span className="text-text-muted text-sm">Loading notifications…</span>
          </div>
        ) : txQuery.isError ? (
          <div className="flex items-center justify-center py-16 px-4 text-center">
            <span className="text-danger text-sm">
              Failed to load notifications:{' '}
              {txQuery.error instanceof Error ? txQuery.error.message : 'Unknown error'}
            </span>
          </div>
        ) : transactions.length === 0 ? (
          <div className="flex items-center justify-center py-16 px-4 text-center">
            <span className="text-text-muted text-sm">
              No notifications for {creatorName} yet.
            </span>
          </div>
        ) : (
          <div className="flex flex-col">
            {transactions.map((tx) => (
              <TransactionRow
                key={tx.id}
                tx={tx}
                isNew={isNew(tx)}
                resolving={resolvingId === tx.id}
                notice={notices[tx.id] ?? null}
                onOpen={() => handleOpen(tx)}
                showCreatorLabel={allCreators}
              />
            ))}
            {/* WR-01: Load more using the next cursor (mirrors admin KpiDashboard pattern) */}
            {nextCursor && (
              <div className="px-3 py-3 text-center border-t border-border">
                <Button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="text-xs"
                >
                  {loadingMore ? 'Loading…' : 'Load more'}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TransactionRow
// ---------------------------------------------------------------------------

interface TransactionRowProps {
  tx: ChatterTransactionDto;
  isNew: boolean;
  /** True while this row's tap-to-thread resolve is in flight. */
  resolving: boolean;
  /** Inline notice shown under the row when open/resolve did not navigate. */
  notice: string | null;
  onOpen: () => void;
  /**
   * All-creators mode (D-13-03): show this row's own creator name (tx.creatorName),
   * reusing the D-13-08 token treatment (ConversationListPane's showCreatorLabel style)
   * so the merged list stays legible. Omitted/false on the single-creator path.
   */
  showCreatorLabel?: boolean;
}

function TransactionRow({ tx, isNew, resolving, notice, onOpen, showCreatorLabel }: TransactionRowProps) {
  const hasThread = !!tx.fanConversationId;
  const fanName = fanDisplayName({
    username: tx.fanUsername,
    fanId: tx.fanMemberId,
  });

  const kindClass = KIND_CLASSES[tx.kind] ?? KIND_CLASSES.other;
  const kindLabel = KIND_LABELS[tx.kind] ?? tx.kind;

  return (
    <div
      className={[
        'border-b border-border last:border-0',
        isNew ? 'border-l-2 border-l-accent' : 'border-l-2 border-l-transparent',
      ].join(' ')}
    >
      <button
        type="button"
        onClick={onOpen}
        disabled={resolving}
        aria-busy={resolving}
        title={hasThread ? `Open ${fanName}'s conversation` : `Open a chat with ${fanName}`}
        className={[
          'w-full text-left px-3 py-2.5 flex gap-3 transition-colors',
          'hover:bg-bg-hover cursor-pointer disabled:cursor-wait',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/60',
        ].join(' ')}
      >
        <div className="relative shrink-0">
          <FanAvatar username={tx.fanUsername} fanId={tx.fanMemberId} size="sm" />
          {isNew && (
            <span
              className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-accent ring-2 ring-bg-card"
              aria-label="New notification"
            />
          )}
        </div>

        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
          {/* All-creators mode (D-13-08 token treatment): this row's own creator name,
              legible in both light and dark. Per-creator rows (showCreatorLabel=false) are
              unaffected. */}
          {showCreatorLabel && tx.creatorName && (
            <span className="text-[11px] font-semibold text-accent font-mono truncate">
              {tx.creatorName}
            </span>
          )}

          {/* Line 1: full fan name + net (payout) amount.
              Owner 2026-07-04: the main number is Maloum's net = creator payout
              (amount_payout_minor). Gross (amount_gross_minor) is never shown. */}
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-text truncate">{fanName}</span>
            <span className="shrink-0 flex items-baseline gap-1 font-mono">
              <span className="text-[10px] uppercase tracking-wide text-text-muted">net</span>
              <span className="text-sm font-semibold text-text">
                {tx.payoutMinor != null ? formatEurMinor(tx.payoutMinor, 2) : '—'}
              </span>
            </span>
          </div>

          {/* Line 2: kind + status · time */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <span
                className={[
                  'shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide',
                  kindClass,
                ].join(' ')}
              >
                {kindLabel}
              </span>
              <span
                className={[
                  'truncate text-[11px] font-mono uppercase tracking-wide',
                  tx.txStatus === 'APPROVED' ? 'text-success' : 'text-warning',
                ].join(' ')}
              >
                {/* WR-04: guard null txStatus (server declares string | null) */}
                {tx.txStatus ?? '—'}
              </span>
            </div>
            <span className="shrink-0 text-xs text-text-subtle font-mono">
              {formatBerlin(tx.occurredAt)}
            </span>
          </div>

          {/* Line 3: thread affordance (payout now shown as the main net figure on line 1) */}
          <div className="flex items-center justify-between gap-2 text-[11px]">
            {resolving ? (
              <span className="inline-flex items-center gap-1 text-accent min-w-0">
                <span
                  className="inline-block w-3 h-3 shrink-0 rounded-full border-2 border-accent/30 border-t-accent animate-spin"
                  aria-hidden="true"
                />
                <span className="truncate">Opening chat…</span>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-text-subtle min-w-0">
                <ChatIcon />
                <span className="truncate">
                  {hasThread ? 'Open conversation' : 'Open a chat with this fan'}
                </span>
              </span>
            )}
          </div>
        </div>
      </button>

      {/* Inline notice when open/resolve did not navigate (e.g. sync disabled / no chat). */}
      {notice && (
        <div className="px-3 pb-2.5 pl-[3.25rem] -mt-1">
          <span className="text-[11px] text-warning" role="status">
            {notice}
          </span>
        </div>
      )}
    </div>
  );
}

function ChatIcon() {
  return (
    <svg
      className="w-3 h-3 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
      />
    </svg>
  );
}
